import { stateful, useState, useEffect } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { confirmDialog, confirmDialogWithCheckbox } from '../confirm-dialog.js';
import { sdrRouter } from '../sdr-router.js';
import { skeletonRows, skeletonStatCards } from '../skeleton.js';
import { toast } from '../toast.js';

/**
 * How long a deleted campaign stays restorable. Mirrors
 * CAMPAIGN_RETENTION_DAYS in the server's helpers/campaign-lifecycle.ts —
 * duplicated because the confirmation dialog has to state the window before
 * any request is made, and a promise the user reads must not be a guess.
 */
const RETENTION_DAYS = 30;

interface CampaignSummary {
  _id: string;
  name: string;
  campaignType: string;
  status: string;
  leadsProcessed?: number;
  leadsFailed?: number;
  totalSent?: number;
  totalDmSent?: number;
  /** Leads on the Do Not Contact list, computed live server-side (campaign-live-counts.ts). */
  totalOptedOut?: number;
  totalReplied?: number;
  totalBounced?: number;
  createdAt: string;
  updatedAt: string;
  launchedAt?: string;
  /** Set while the campaign is archived; cleared on unarchive. */
  archivedAt?: string;
  projectId?: string;
}

interface ProjectSummary {
  _id: string;
  name: string;
  description?: string;
  isDefault?: boolean;
}

export const Dashboard = stateful(() => {
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  // null = showing the Projects grid; set = drilled into one project's campaigns.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  // The name/description dialog, shared by both flows: a ProjectSummary means
  // "edit this one", the 'new' sentinel means "create one". One dialog rather
  // than two near-identical copies that would drift apart.
  const [editingProject, setEditingProject] = useState<ProjectSummary | 'new' | null>(null);
  const [projName, setProjName] = useState('');
  const [projDescription, setProjDescription] = useState('');
  const [savingProject, setSavingProject] = useState(false);
  // Archived campaigns are kept out of the list by default — that's the point
  // of archiving — but stay one click away rather than hidden outright.
  const [showArchived, setShowArchived] = useState(false);
  // Campaign id whose archive/unarchive/delete request is in flight.
  const [rowBusy, setRowBusy] = useState('');
  // Project id whose delete request is in flight.
  const [deletingProjectId, setDeletingProjectId] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('/api/campaigns');
        const data = await resp.json();
        if (data.ok && data.campaigns) {
          // Ensure every campaign has a valid string _id
          setCampaigns(
            data.campaigns
              .map((c: any) => ({ ...c, _id: typeof c._id === 'string' ? c._id : String(c._id ?? '') }))
              .filter((c: CampaignSummary) => c._id.length > 0),
          );
        }
      } catch {
        toast.error('Could not load campaigns. Please refresh.');
      }
      setLoading(false);
    })();
    (async () => {
      try {
        const resp = await fetch('/api/projects');
        const data = await resp.json();
        if (data.ok) setProjects(data.projects);
      } catch {
        /* ignore — project filter/badges just stay empty */
      }
    })();
  }, []);

  const defaultProjectId = projects.find(p => p.isDefault)?._id;
  const effectiveProjectId = (c: CampaignSummary): string | undefined => c.projectId ?? defaultProjectId;
  const sortedProjects = projects
    .slice()
    .sort((a, b) => (a.isDefault ? -1 : b.isDefault ? 1 : a.name.localeCompare(b.name)));
  const campaignsForProject = (projectId: string) => campaigns.filter(c => effectiveProjectId(c) === projectId);
  const selectedProject = selectedProjectId ? projects.find(p => p._id === selectedProjectId) : null;
  const scopedCampaigns = selectedProjectId ? campaignsForProject(selectedProjectId) : campaigns;
  const archivedCount = scopedCampaigns.filter(c => c.status === 'archived').length;
  // The stat cards still count archived campaigns — they exist and their
  // numbers are real. Only the list hides them.
  const listedCampaigns = showArchived ? scopedCampaigns : scopedCampaigns.filter(c => c.status !== 'archived');

  const statusBadge = (status: string) => {
    const classes: Record<string, string> = {
      active: 'badge-success',
      paused: 'badge-warning',
      draft: 'badge-ghost',
      generating: 'badge-info',
      ready_to_launch: 'badge-warning',
      preparing_to_launch: 'badge-warning',
      completed: 'badge-info',
      archived: 'badge-neutral',
      error: 'badge-error',
    };
    const labels: Record<string, string> = {
      ready_to_launch: 'ready to launch',
      preparing_to_launch: 'cool-down',
    };
    return html`<span class="badge ${classes[status] ?? 'badge-ghost'} shrink-0 whitespace-nowrap"
      >${labels[status] ?? status}</span
    >`;
  };

  const typeBadge = (type: string) =>
    type === 'email'
      ? html`<span class="badge badge-outline shrink-0 gap-1 whitespace-nowrap"
          ><span class="icon-[material-symbols--mail] text-sm"></span> Email</span
        >`
      : type === 'multi'
      ? html`<span class="badge badge-outline shrink-0 gap-1 whitespace-nowrap"
          ><span class="icon-[material-symbols--hub] text-sm"></span> Cross-Channel</span
        >`
      : html`<span class="badge badge-outline shrink-0 gap-1 whitespace-nowrap"
          ><span class="icon-[material-symbols--share] text-sm"></span> LinkedIn</span
        >`;

  // `value` takes a string as well as a number because one of these figures is
  // not always allowed to be a number: `?? 0` is how "nothing tracks this" was
  // rendered as "nobody opened it", on every campaign in this list.
  const stat = (label: string, value: number | string | undefined, title?: string) =>
    html`<div class="flex flex-col items-center" title=${title ?? nothing}>
      <span class="text-lg font-semibold">${value ?? 0}</span>
      <span class="text-base-content/60 text-xs">${label}</span>
    </div>`;

  const handleCampaignClick = (c: CampaignSummary) => {
    const id = typeof c._id === 'string' ? c._id.trim() : '';
    if (!id) {
      console.warn('[dashboard] Skipping campaign click — missing _id', c);
      return;
    }
    sdrRouter.goTo('campaignDetail', { params: { campaignId: id } } as any);
  };

  const editCampaign = (e: Event, c: CampaignSummary) => {
    e.stopPropagation();
    const id = typeof c._id === 'string' ? c._id.trim() : '';
    if (!id) return;
    sdrRouter.goTo('editCampaign', { params: { campaignId: id } } as any);
  };

  // Mirrors the gate on campaign-detail.ts's own Edit button — active/
  // archived/error campaigns don't show it here either, so nobody can jump
  // into the wizard by accident on a campaign that's already sending.
  // 'paused' IS included so the Leads step's top-up (CSV/HubSpot/Apollo) is
  // reachable while paused — other wizard steps still reject edits there.
  const canEditFromDashboard = (status: string | undefined) =>
    status === 'draft' || status === 'ready_to_launch' || status === 'preparing_to_launch' || status === 'paused';

  const startEditProject = (p: ProjectSummary) => {
    setProjName(p.name ?? '');
    setProjDescription(p.description ?? '');
    setEditingProject(p);
  };

  const startNewProject = () => {
    setProjName('');
    setProjDescription('');
    setEditingProject('new');
  };

  /**
   * Deletes the grouping, never the campaigns. Everything inside moves to the
   * Default Project — which is safe by construction, since the default project
   * is the one place duplicate leads across campaigns are allowed.
   */
  const deleteProject = async (e: Event, p: ProjectSummary) => {
    e.stopPropagation();
    const count = campaignsForProject(p._id).length;
    if (
      !(await confirmDialog(
        count === 0
          ? `Delete the project “${p.name}”? It has no campaigns in it.`
          : `Delete the project “${p.name}”? Its ${count} campaign${count === 1 ? '' : 's'} ${
              count === 1 ? 'is' : 'are'
            } not deleted — ${count === 1 ? 'it moves' : 'they move'} to the Default Project. Only the grouping is lost.`,
        { title: 'Delete Project', confirmLabel: 'Delete project', confirmClass: 'btn-error' },
      ))
    )
      return;
    setDeletingProjectId(p._id);
    try {
      const resp = await fetch(`/api/projects/${encodeURIComponent(p._id)}`, { method: 'DELETE' });
      const data = await resp.json().catch(() => null);
      if (!resp.ok || !data?.ok) {
        toast.error(data?.error ?? 'Failed to delete project. Please try again.');
        return;
      }
      // Re-point the moved campaigns locally so the Default Project's count is
      // right immediately, without a full reload.
      const newProjectId = data.defaultProjectId ?? defaultProjectId;
      setCampaigns(campaigns.map(c => (effectiveProjectId(c) === p._id ? { ...c, projectId: newProjectId } : c)));
      setProjects(projects.filter(x => x._id !== p._id));
      // If we were inside the project we just deleted, there's nothing left to
      // look at — go back to the grid.
      if (selectedProjectId === p._id) setSelectedProjectId(null);
      // Deleting can also be triggered from inside the edit dialog; that
      // dialog is now editing something that no longer exists, so close it.
      setEditingProject(null);
      toast.success(
        data.movedCampaigns > 0
          ? `Project deleted — ${data.movedCampaigns} campaign${
              data.movedCampaigns === 1 ? '' : 's'
            } moved to the Default Project.`
          : 'Project deleted.',
      );
    } catch {
      toast.error('Network error — project was not deleted.');
    } finally {
      setDeletingProjectId('');
    }
  };

  const saveProject = async () => {
    if (!editingProject || !projName.trim()) {
      toast.error('Project name cannot be empty');
      return;
    }
    const isNew = editingProject === 'new';
    setSavingProject(true);
    try {
      const body = JSON.stringify({ name: projName.trim(), description: projDescription.trim() });
      const resp = isNew
        ? await fetch('/api/projects', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body })
        : await fetch(`/api/projects/${encodeURIComponent(editingProject._id)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body,
          });
      const data = await resp.json();
      if (!data.ok) {
        toast.error(data.error ?? (isNew ? 'Failed to create project' : 'Failed to save project'));
        return;
      }
      setProjects(
        isNew
          ? [...projects, data.project]
          : projects.map(p => (p._id === (editingProject as ProjectSummary)._id ? { ...p, ...data.project } : p)),
      );
      setEditingProject(null);
      toast.success(isNew ? 'Project created.' : 'Project saved.');
    } catch {
      toast.error(isNew ? 'Failed to create project (network error)' : 'Failed to save project (network error)');
    }
    setSavingProject(false);
  };

  // Archiving is offered wherever the campaign detail page offers it, so the
  // two views can't disagree about what's archivable.
  const canArchive = (status: string | undefined) => status !== 'archived' && status !== 'draft';

  /**
   * Delete = soft delete. The campaign leaves the dashboard and stops sending,
   * but it is recoverable from Settings › Deleted Campaigns for
   * RETENTION_DAYS. The dialog says so — "cannot be undone" was the old copy
   * and is now simply untrue.
   */
  const deleteCampaign = async (e: Event, c: CampaignSummary) => {
    e.stopPropagation();
    const label = c.name || 'this untitled draft';
    const canRecall = c.campaignType === 'linkedin' || c.campaignType === 'multi';
    const message = `Delete “${label}”? It stops sending and disappears from your dashboard. You can restore it for ${RETENTION_DAYS} days under Settings › Deleted Campaigns — after that it is deleted permanently.`;
    let recall = false;
    if (canRecall) {
      const res = await confirmDialogWithCheckbox(message, {
        title: 'Delete Campaign',
        confirmLabel: 'Delete campaign',
        confirmClass: 'btn-error',
        checkboxLabel:
          'Also recall this campaign’s connection requests — cancel the pending ones and withdraw its already-sent invitations on LinkedIn. This can’t be undone by Restore.',
      });
      if (!res.confirmed) return;
      recall = res.checked;
    } else if (
      !(await confirmDialog(message, {
        title: 'Delete Campaign',
        confirmLabel: 'Delete campaign',
        confirmClass: 'btn-error',
      }))
    ) {
      return;
    }
    setRowBusy(c._id);
    try {
      const resp = await fetch(
        `/api/campaigns/${encodeURIComponent(c._id)}${recall ? '?recallInvitations=true' : ''}`,
        { method: 'DELETE' },
      );
      const data = await resp.json().catch(() => null);
      if (!resp.ok) {
        toast.error(data?.error ?? 'Failed to delete campaign. Please try again.');
        return;
      }
      setCampaigns(campaigns.filter(x => x._id !== c._id));
      toast.success(`Campaign deleted — restorable for ${data?.retentionDays ?? RETENTION_DAYS} days in Settings.`);
      if (recall && data?.recalled) {
        const r = data.recalled;
        toast.info(`Connection requests recalled: ${r.sentRecalled} withdrawn, ${r.jobsCancelled} pending cancelled.`);
      }
      if (data?.warning) {
        toast.warning(data.warning);
      }
    } catch {
      toast.error('Network error — campaign was not deleted.');
    } finally {
      setRowBusy('');
    }
  };

  const archiveCampaign = async (e: Event, c: CampaignSummary) => {
    e.stopPropagation();
    const label = c.name || 'this untitled draft';
    if (
      !(await confirmDialog(
        `Archive “${label}”? It stops sending and moves out of your campaign list. Nothing is deleted — you can unarchive it at any time.`,
        { title: 'Archive Campaign', confirmLabel: 'Archive', confirmClass: 'btn-warning' },
      ))
    )
      return;
    setRowBusy(c._id);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(c._id)}/archive`, { method: 'POST' });
      const data = await resp.json().catch(() => null);
      if (!resp.ok || !data?.ok) {
        toast.error(data?.error ?? 'Failed to archive campaign. Please try again.');
        return;
      }
      setCampaigns(campaigns.map(x => (x._id === c._id ? { ...x, status: 'archived' } : x)));
      toast.success('Campaign archived.');
      if (data.warning) toast.warning(data.warning);
    } catch {
      toast.error('Network error — campaign was not archived.');
    } finally {
      setRowBusy('');
    }
  };

  // No confirmation: unarchiving destroys nothing and the campaign comes back
  // paused, so the worst case is one more click to re-archive it.
  const unarchiveCampaign = async (e: Event, c: CampaignSummary) => {
    e.stopPropagation();
    setRowBusy(c._id);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(c._id)}/unarchive`, { method: 'POST' });
      const data = await resp.json().catch(() => null);
      if (!resp.ok || !data?.ok) {
        toast.error(data?.error ?? 'Failed to unarchive campaign. Please try again.');
        return;
      }
      setCampaigns(campaigns.map(x => (x._id === c._id ? { ...x, status: data.status ?? 'paused' } : x)));
      toast.success(
        data.status === 'paused'
          ? 'Campaign unarchived — it is paused, resume it when you want it sending again.'
          : 'Campaign unarchived.',
      );
    } catch {
      toast.error('Network error — campaign was not unarchived.');
    } finally {
      setRowBusy('');
    }
  };

  return html`
    <div class="mx-auto flex max-w-5xl flex-col gap-6 p-6">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <span class="bg-primary/10 text-primary flex h-11 w-11 items-center justify-center rounded-xl">
            <span class="icon-[material-symbols--${selectedProjectId ? 'folder' : 'campaign'}] text-2xl"></span>
          </span>
          <div>
            ${
              selectedProject
                ? html`
                  <div class="flex items-center gap-2">
                    <button class="btn btn-ghost btn-xs gap-1" @click=${() => setSelectedProjectId(null)}>
                      <span class="icon-[material-symbols--arrow-back] text-sm"></span> Projects
                    </button>
                  </div>
                  <h1 class="text-3xl font-bold">
                    ${selectedProject.isDefault ? 'Default Project' : selectedProject.name}
                  </h1>
                  ${
                    selectedProject.description
                      ? html`<p class="text-base-content/70">${selectedProject.description}</p>`
                      : nothing
                  }
                `
                : html`
                  <div class="flex items-center gap-2">
                    <h1 class="text-3xl font-bold">Projects</h1>
                    <button
                      class="btn btn-ghost btn-sm btn-square"
                      title="New project"
                      aria-label="New project"
                      @click=${startNewProject}
                    >
                      <span class="icon-[material-symbols--add] text-xl" aria-hidden="true"></span>
                    </button>
                  </div>
                  <p class="text-base-content/70">Campaigns are organised into projects — pick one to see inside.</p>
                `
            }
          </div>
        </div>
        <button class="btn btn-primary gap-2" @click=${() => sdrRouter.goTo('newCampaign')}>
          <span class="icon-[material-symbols--add]"></span>
          New Campaign
        </button>
      </div>

      ${loading ? skeletonStatCards(5) : html`<div class="grid grid-cols-2 gap-4 sm:grid-cols-5">
        <div class="card bg-base-200 shadow-sm">
          <div class="card-body items-center text-center">
            <p class="text-3xl font-bold">${scopedCampaigns.length}</p>
            <p class="text-base-content/70 text-sm">${selectedProjectId ? 'Campaigns' : 'Total Campaigns'}</p>
          </div>
        </div>
        <div class="card bg-base-200 shadow-sm">
          <div class="card-body items-center text-center">
            <p class="text-3xl font-bold">${scopedCampaigns.reduce((s, c) => s + (c.leadsProcessed ?? 0), 0)}</p>
            <p class="text-base-content/70 text-sm">Total Leads</p>
          </div>
        </div>
        <div class="card bg-base-200 shadow-sm">
          <div class="card-body items-center text-center">
            <p class="text-3xl font-bold">${scopedCampaigns.reduce((s, c) => {
              if (c.campaignType === 'linkedin') return s + (c.totalDmSent ?? 0);
              if (c.campaignType === 'multi') return s + (c.totalSent ?? 0) + (c.totalDmSent ?? 0);
              return s + (c.totalSent ?? 0);
            }, 0)}</p>
            <p class="text-base-content/70 text-sm">Messages Sent</p>
          </div>
        </div>
        <div class="card bg-base-200 shadow-sm">
          <div class="card-body items-center text-center">
            <p class="text-3xl font-bold">${scopedCampaigns.reduce((s, c) => s + (c.totalReplied ?? 0), 0)}</p>
            <p class="text-base-content/70 text-sm">Replies</p>
          </div>
        </div>
        <div class="card bg-base-200 shadow-sm">
          <div class="card-body items-center text-center">
            <p class="text-3xl font-bold">${scopedCampaigns.filter(c => c.status === 'active').length}</p>
            <p class="text-base-content/70 text-sm">Active</p>
          </div>
        </div>
      </div>`}

      ${loading
        ? skeletonRows(3)
        : selectedProjectId === null
          ? html`
              <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                ${sortedProjects.map(p => {
                  const count = campaignsForProject(p._id).length;
                  return html`
                    <div
                      class="card bg-base-100 cursor-pointer shadow-sm transition hover:shadow-md"
                      @click=${() => setSelectedProjectId(p._id)}
                    >
                      <div class="card-body gap-2">
                        <div class="flex items-center gap-3">
                          <span
                            class="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                          >
                            <span class="icon-[material-symbols--folder] text-xl"></span>
                          </span>
                          <div class="min-w-0 flex-1">
                            <h2 class="truncate text-lg font-semibold">
                              ${p.isDefault ? 'Default Project' : p.name}
                            </h2>
                            <p class="text-base-content/60 text-xs">
                              ${count} campaign${count === 1 ? '' : 's'}
                            </p>
                          </div>
                          ${
                            // The default project is a system container whose
                            // name the dashboard always renders as "Default
                            // Project" — renaming it would have no visible
                            // effect, so it isn't offered. Deleting it is
                            // refused server-side for the same reason: it is
                            // where campaigns without a project live.
                            p.isDefault
                              ? nothing
                              : html`
                                <button
                                  class="btn btn-ghost btn-xs btn-square shrink-0"
                                  title="Edit project name and description"
                                  aria-label="Edit project ${p.name}"
                                  @click=${(e: Event) => {
                                    e.stopPropagation();
                                    startEditProject(p);
                                  }}
                                >
                                  <span class="icon-[material-symbols--edit] text-base"></span>
                                </button>
                                <button
                                  class="btn btn-ghost btn-xs btn-square shrink-0 ${
                                    deletingProjectId === p._id ? 'btn-disabled' : ''
                                  }"
                                  title="Delete project — its campaigns move to the Default Project"
                                  aria-label="Delete project ${p.name}"
                                  @click=${(e: Event) => void deleteProject(e, p)}
                                >
                                  ${
                                    deletingProjectId === p._id
                                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                                      : html`<span class="icon-[material-symbols--delete-outline] text-error text-base"></span>`
                                  }
                                </button>
                              `
                          }
                        </div>
                        ${
                          p.description
                            ? html`<p class="text-base-content/70 line-clamp-2 text-sm">${p.description}</p>`
                            : nothing
                        }
                      </div>
                    </div>
                  `;
                })}
              </div>
            `
          : scopedCampaigns.length === 0
            ? html`
                <div class="card bg-base-100 shadow-sm">
                  <div class="card-body items-center gap-4 py-16 text-center">
                    <span class="icon-[material-symbols--campaign] text-base-content/30 text-6xl" aria-hidden="true"></span>
                    <div>
                      <h2 class="text-xl font-semibold">No campaigns in this project yet</h2>
                      <p class="text-base-content/60 mt-1">Start your first one here to begin.</p>
                    </div>
                    <button class="btn btn-primary gap-2" @click=${() => sdrRouter.goTo('newCampaign')}>
                      <span class="icon-[material-symbols--rocket-launch]" aria-hidden="true"></span>
                      Start your first campaign
                    </button>
                  </div>
                </div>
              `
            : html`
                <div class="flex flex-col gap-3">
                  ${archivedCount > 0
                    ? html`
                      <div class="flex justify-end">
                        <button
                          class="btn btn-ghost btn-xs gap-1.5"
                          aria-pressed=${showArchived ? 'true' : 'false'}
                          @click=${() => setShowArchived(!showArchived)}
                        >
                          <span
                            class="icon-[material-symbols--${showArchived ? 'visibility-off' : 'inventory-2'}] text-sm"
                            aria-hidden="true"
                          ></span>
                          ${showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
                        </button>
                      </div>
                    `
                    : nothing}
                  ${listedCampaigns.length === 0
                    ? html`
                      <div class="card bg-base-100 shadow-sm">
                        <div class="card-body items-center gap-2 py-10 text-center">
                          <span class="icon-[material-symbols--inventory-2] text-base-content/30 text-4xl" aria-hidden="true"></span>
                          <p class="text-base-content/60 text-sm">
                            Every campaign in this project is archived. Use “Show archived” above to see them.
                          </p>
                        </div>
                      </div>
                    `
                    : nothing}
                  ${listedCampaigns.map(
                    c => html`
                      <div
                        class="card bg-base-100 cursor-pointer shadow-sm transition hover:shadow-md"
                        @click=${() => handleCampaignClick(c)}
                      >
                        <div class="card-body flex-row items-center gap-4">
                          <div class="flex min-w-0 flex-1 flex-col gap-1">
                            <div class="flex min-w-0 items-center gap-2">
                              <span class="min-w-0 truncate text-lg font-semibold">${c.name || 'Untitled Draft'}</span>
                              ${statusBadge(c.status)} ${typeBadge(c.campaignType)}
                            </div>
                            <span class="text-base-content/50 text-xs">
                              ${c.status === 'archived' && c.archivedAt
                                ? `Archived ${new Date(c.archivedAt).toLocaleDateString()}`
                                : c.launchedAt
                                  ? `Launched ${new Date(c.launchedAt).toLocaleDateString()}`
                                  : `Created ${new Date(c.createdAt).toLocaleDateString()}`}
                            </span>
                          </div>
                          <div class="flex gap-6">
                            ${stat('Leads', c.leadsProcessed)}
                            ${stat('Sent', c.totalSent)}
                            ${stat('Opted Out', c.totalOptedOut)}
                            ${stat('Replied', c.totalReplied)}
                          </div>
                          ${canEditFromDashboard(c.status)
                            ? html`
                              <button
                                class="btn btn-ghost btn-sm btn-square"
                                @click=${(e: Event) => editCampaign(e, c)}
                                title="Edit campaign"
                                aria-label="Edit campaign ${c.name || 'Untitled draft'}"
                              >
                                <span class="icon-[material-symbols--edit]" aria-hidden="true"></span>
                              </button>
                            `
                            : nothing}
                          ${c.status === 'archived'
                            ? html`
                              <button
                                class="btn btn-ghost btn-sm btn-square ${rowBusy === c._id ? 'btn-disabled' : ''}"
                                @click=${(e: Event) => unarchiveCampaign(e, c)}
                                title="Unarchive campaign"
                                aria-label="Unarchive campaign ${c.name || 'Untitled draft'}"
                              >
                                <span class="icon-[material-symbols--unarchive]" aria-hidden="true"></span>
                              </button>
                            `
                            : canArchive(c.status)
                              ? html`
                                <button
                                  class="btn btn-ghost btn-sm btn-square ${rowBusy === c._id ? 'btn-disabled' : ''}"
                                  @click=${(e: Event) => archiveCampaign(e, c)}
                                  title="Archive campaign — keeps it, just out of the way"
                                  aria-label="Archive campaign ${c.name || 'Untitled draft'}"
                                >
                                  <span class="icon-[material-symbols--archive]" aria-hidden="true"></span>
                                </button>
                              `
                              : nothing}
                          <button
                            class="btn btn-ghost btn-sm btn-square ${rowBusy === c._id ? 'btn-disabled' : ''}"
                            @click=${(e: Event) => deleteCampaign(e, c)}
                            title="Delete campaign — restorable for ${RETENTION_DAYS} days"
                            aria-label="Delete campaign ${c.name || 'Untitled draft'}"
                          >
                            <span class="icon-[material-symbols--delete-outline] text-error" aria-hidden="true"></span>
                          </button>
                        </div>
                      </div>
                    `,
                  )}
                </div>
              `}

      <!-- Project name / description dialog — same form for create and edit -->
      ${
        editingProject
          ? html`
            <div
              class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              @click=${(e: MouseEvent) => {
                if (e.target === e.currentTarget) setEditingProject(null);
              }}
            >
              <div class="bg-base-100 flex w-full max-w-lg flex-col overflow-hidden rounded-2xl shadow-2xl">
                <div class="border-base-300 flex items-center justify-between border-b px-5 py-3">
                  <div class="flex items-center gap-2">
                    <span class="icon-[material-symbols--folder] text-primary text-xl"></span>
                    <h3 class="font-semibold">${editingProject === 'new' ? 'New Project' : 'Edit Project'}</h3>
                  </div>
                  <button class="btn btn-ghost btn-sm btn-circle" @click=${() => setEditingProject(null)}>
                    <span class="icon-[material-symbols--close]"></span>
                  </button>
                </div>
                <div class="flex flex-col gap-3 p-5">
                  <div class="form-control">
                    <span class="label-text mb-1 text-xs font-medium">
                      Project Name <span class="text-error">*</span>
                    </span>
                    <input
                      class="input input-bordered input-sm w-full"
                      .value=${projName}
                      placeholder="e.g., Gradias Project"
                      @input=${(e: Event) => setProjName((e.target as HTMLInputElement).value)}
                    />
                  </div>
                  <div class="form-control">
                    <span class="label-text mb-1 text-xs font-medium">Description</span>
                    <textarea
                      class="textarea textarea-bordered textarea-sm w-full"
                      rows="4"
                      .value=${projDescription}
                      placeholder="What is this project about?"
                      @input=${(e: Event) => setProjDescription((e.target as HTMLTextAreaElement).value)}
                    ></textarea>
                  </div>
                </div>
                <div class="border-base-300 flex items-center justify-between gap-2 border-t px-5 py-3">
                  ${
                    // Delete lives here too, not only on the card: the edit
                    // dialog is where people go when they want to *change* a
                    // project, and "get rid of it" is the same intent. Kept
                    // visually apart from Cancel/Save so it can't be hit by
                    // reflex — and never offered for the default project,
                    // which the server refuses to delete anyway.
                    editingProject !== 'new' && !editingProject.isDefault
                      ? html`
                        <button
                          class="btn btn-ghost btn-sm text-error gap-1.5 ${
                            deletingProjectId === editingProject._id ? 'btn-disabled' : ''
                          }"
                          title="Delete this project — its campaigns move to the Default Project"
                          @click=${(e: Event) => void deleteProject(e, editingProject)}
                        >
                          ${
                            deletingProjectId === editingProject._id
                              ? html`<span class="loading loading-spinner loading-xs"></span>`
                              : html`<span class="icon-[material-symbols--delete-outline] text-base"></span>`
                          }
                          Delete Project
                        </button>
                      `
                      : html`<span></span>`
                  }
                  <div class="flex gap-2">
                    <button class="btn btn-ghost btn-sm" @click=${() => setEditingProject(null)}>Cancel</button>
                    <button
                      class="btn btn-primary btn-sm gap-1.5 ${savingProject || !projName.trim() ? 'btn-disabled' : ''}"
                      @click=${() => void saveProject()}
                    >
                      ${
                        savingProject
                          ? html`<span class="loading loading-spinner loading-xs"></span>`
                          : editingProject === 'new'
                            ? html`<span class="icon-[material-symbols--add] text-base"></span>`
                            : html`<span class="icon-[material-symbols--save] text-base"></span>`
                      }
                      ${editingProject === 'new' ? 'Create Project' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          `
          : nothing
      }
    </div>
  `;
});
